import { classifyInvoiceFile, MAX_JPEG_BYTES } from './invoiceFile';

const MAX_EDGE = 2500;
const JPEG_QUALITIES = [0.85, 0.7, 0.55];

export type PreparedInvoice = {
  blob: Blob;
  previewUrl: string;
  originalName: string;
  pdfFirstPageOnly: boolean;
};

function publicUrl(filePath: string): string {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/${filePath.replace(/^\//, '')}`;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('לא הצלחנו לפתוח את התמונה.'));
    image.src = src;
  });
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('לא הצלחנו להמיר את הקובץ ל-JPEG.'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality
    );
  });
}

function drawToCanvas(sourceWidth: number, sourceHeight: number, draw: (ctx: CanvasRenderingContext2D, width: number, height: number) => void): HTMLCanvasElement {
  const scale = Math.min(1, MAX_EDGE / Math.max(sourceWidth, sourceHeight, 1));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('לא הצלחנו לעבד את התמונה.');
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  draw(ctx, width, height);
  return canvas;
}

async function jpegFromCanvas(canvas: HTMLCanvasElement): Promise<Blob> {
  let blob: Blob | null = null;
  for (const quality of JPEG_QUALITIES) {
    blob = await canvasToJpegBlob(canvas, quality);
    if (blob.size <= MAX_JPEG_BYTES) {
      return blob;
    }
  }
  throw new Error('התמונה גדולה מדי אחרי ההמרה. נסו קובץ קטן יותר.');
}

async function jpegFromImageFile(file: File): Promise<Blob> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    const canvas = drawToCanvas(image.naturalWidth || image.width, image.naturalHeight || image.height, (ctx, width, height) => {
      ctx.drawImage(image, 0, 0, width, height);
    });
    return jpegFromCanvas(canvas);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function pdfOpenError(error: unknown): Error {
  const message = error instanceof Error ? error.message : String(error || '');
  if (/password/i.test(message)) {
    return new Error('לא ניתן לקרוא PDF מוגן בסיסמה.');
  }
  if (/fetch|network|load script|worker/i.test(message)) {
    return new Error('לא הצלחנו לקרוא את ה-PDF. נסו שוב, או שמרו את העמוד הראשון כ-JPEG.');
  }
  return new Error('לא הצלחנו לפתוח את קובץ ה-PDF.');
}

async function jpegFromPdfFile(file: File): Promise<Blob> {
  const pdfjsModule = await import('pdfjs-dist/legacy/build/pdf') as any;
  const pdfjs = pdfjsModule.getDocument ? pdfjsModule : pdfjsModule.default;
  pdfjs.GlobalWorkerOptions.workerSrc = publicUrl('pdf.worker.min.js');

  const data = new Uint8Array(await file.arrayBuffer());
  let pdf;
  try {
    pdf = await pdfjs.getDocument({
      data,
      cMapUrl: publicUrl('pdfjs/cmaps/'),
      cMapPacked: true,
      standardFontDataUrl: publicUrl('pdfjs/standard_fonts/'),
      useSystemFonts: true,
    }).promise;
  } catch (error) {
    throw pdfOpenError(error);
  }

  const page = await pdf.getPage(1);
  const baseViewport = page.getViewport({ scale: 2 });
  const fit = Math.min(1, MAX_EDGE / Math.max(baseViewport.width, baseViewport.height, 1));
  const viewport = page.getViewport({ scale: 2 * fit });
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(viewport.width));
  canvas.height = Math.max(1, Math.round(viewport.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('לא הצלחנו לעבד את ה-PDF.');
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  try {
    await page.render({ canvasContext: ctx, viewport }).promise;
  } catch (error) {
    throw pdfOpenError(error);
  }
  return jpegFromCanvas(canvas);
}

export async function prepareInvoiceFile(file: File): Promise<PreparedInvoice> {
  const check = classifyInvoiceFile(file);
  if (!check.ok) {
    throw new Error(check.error);
  }

  const blob = check.kind === 'pdf' ? await jpegFromPdfFile(file) : await jpegFromImageFile(file);
  return {
    blob,
    previewUrl: URL.createObjectURL(blob),
    originalName: file.name,
    pdfFirstPageOnly: check.kind === 'pdf',
  };
}
